import { defineQuery } from "@rocicorp/zero";
import { z } from "zod";
import type {} from "../zero-context";
import { zql } from "../schema";
import { channelMemberGuard } from "./access-filters";

// Phase 6: agent-queryability shared queries for kind='calendar' messages.
// Imperative one-shot reads (zero.run) — NOT subscriptions. Channel-
// membership is enforced via channelMemberGuard on the outer messages
// scope so a caller never sees a calendar event from a channel they
// can't already access.
//
// NOTE (no current consumers): these queries are scaffolding for future
// agent / Team Brain v1 paths. When they become hot, the messages outer
// scope may need a `(kind, id)` composite index for zero-cache —
// migration 092's `idx_messages_calendar_start_at` is a PARTIAL index
// (WHERE kind='calendar') and per .claude/rules/zero-schema.md partial
// indexes don't replicate to zero-cache's SQLite replica. Today the
// agent surface uses the server-side helpers in mcp-operations.ts
// instead, which DO benefit from those partial indexes via Postgres.

export const calendarEventsQueries = {
  // Calendar messages where `userId` is an attendee. Joins via the
  // `calendar_attendees` side table — populated by builders at
  // insert time (per packages/shared/src/message-builders.ts).
  withAttendee: defineQuery(
    z.object({
      userId: z.string(),
      since: z.number().optional(),
      limit: z.number().default(100),
    }),
    ({ args: { userId, since, limit }, ctx: { sub } }) => {
      const base = zql.messages
        .where("kind", "calendar")
        .whereExists("calendar_attendees", (caq) =>
          caq.where("user_id", userId),
        )
        .whereExists("channel", channelMemberGuard(sub));
      const filtered = since ? base.where("created_at", ">=", since) : base;
      return filtered
        .orderBy("created_at", "desc")
        .orderBy("id", "desc")
        .limit(limit)
        .related("calendar_attendees");
    },
  ),
  // Calendar messages in a channel with start_at > now, ordered by
  // start_at ascending. Useful for "upcoming events in #planning".
  upcomingInChannel: defineQuery(
    z.object({
      channelId: z.string(),
      limit: z.number().default(50),
    }),
    ({ args: { channelId, limit }, ctx: { sub } }) =>
      zql.messages
        .where("channel_id", channelId)
        .where("kind", "calendar")
        .where("calendar_start_at", ">", Date.now())
        .whereExists("channel", channelMemberGuard(sub))
        .orderBy("calendar_start_at", "asc")
        .orderBy("id", "asc")
        .limit(limit)
        .related("calendar_attendees"),
  ),
};
